import { makeObservable, observable, action, runInAction } from "mobx";

import { getProjects, getProjectDetail } from "../api";
import { setWebBridge } from "../utils/webBridge";
import { convertStringToObject } from "../utils/convert";
import { GlobalErrorType, IApiHeader } from "./appStoreTypes";
import {
  IDetailState,
  IInfoState,
  initialInfoState,
  IDownloadState,
  initialDownloadState,
  IInstallState,
  initialInstallState,
  IErrorInfo,
  IErrorState,
  IUpdateState,
} from "./detailStoreTypes";

export default class DetailStore {
  constructor() {
    makeObservable(this, {
      detailState: observable,
      onCopyDetailState: action,
      onGetDetailItem: action,
      onUpdateDetailList: action,

      detailStates: observable,
      onRevertDetailState: action,

      infoState: observable,
      onUpdateInfoState: action,
      onResetInfoState: action,

      downloadState: observable,
      onDownloadStart: action,
      onDownloadCancel: action,
      onDownloadProgress: action,

      installState: observable,
      onInstallProgress: action,
      onInstallComplete: action,
      completedProjects: observable,

      updateState: observable,
      onUpdateStart: action,
      onUpdateCancel: action,

      errorState: observable,
      onErrorState: action,
      onResetErrorState: action,

      detailErrorState: observable,
      onUpdateDetailError: action,
    });
  }

  detailState: IDetailState = {
    list: [],
    page: 1,
    display: 20,
    isLoadingList: true,
    searchType: "",
  };
  onCopyDetailState = (state: IDetailState) => {
    this.detailState = state;
    this.detailStates.push(state);
  };
  onGetDetailItem = async (apiHeader: IApiHeader, projectId: string) => {
    const detailState = this.detailState;
    detailState.isLoadingList = true;

    try {
      const { data, error, code } = await getProjectDetail({
        ...apiHeader,
        projectId,
      });

      // 없는 detail Id의 경우 알 수 없는 에러로 처리
      if (code === 404) {
        throw console.error("not found detail unknown error");
      }

      if (error) {
        return this.onUpdateDetailError(GlobalErrorType.NET_WORK);
      }

      runInAction(() => {
        if (data) detailState.selectedProject = data;
        detailState.isLoadingList = false;
      });
    } catch {
      this.onUpdateDetailError(GlobalErrorType.UNKNOWN);
    }
  };
  onUpdateDetailList = async (apiHeader: IApiHeader) => {
    const detailState = this.detailState;

    detailState.isLoadingList = true;

    const { page, display, categoryId, tag } = detailState;

    console.warn("detailStore", detailState.searchType);

    try {
      let type: "projects" | "v1_search" | "v2_search";
      if (detailState.searchType === "hashtag") {
        type = "v1_search";
      } else if (detailState.searchType === "search") {
        type = "v2_search";
      } else {
        type = "projects";
      }
      const { data, error } = await getProjects({
        ...apiHeader,
        page,
        display,
        categoryId,
        tag,
        type,
      });

      if (error) {
        return this.onUpdateDetailError(GlobalErrorType.NET_WORK);
      }

      runInAction(() => {
        if (!data) throw console.error("onUpdateDetailList is fail!");
        if (data.length === 0)
          return console.log("onUpdateDetailList is empty");

        if (page === 1) {
          detailState.list = data;
        } else {
          detailState.list = detailState.list.concat(data);
        }
        detailState.page = page + 1;

        // update detailStates
        this.detailStates.pop();
        this.detailStates.push(detailState);
      });
    } catch {
      this.onUpdateDetailError(GlobalErrorType.UNKNOWN);
    } finally {
      runInAction(() => {
        detailState.isLoadingList = false;
      });
    }
  };

  detailStates: IDetailState[] = [];
  onRevertDetailState = () => {
    const detailStates = this.detailStates;
    detailStates.pop();
    if (detailStates.length === 0) return;
    this.detailState = detailStates[detailStates.length - 1];
  };

  infoState: IInfoState = initialInfoState;
  onUpdateInfoState = (state: IInfoState) => {
    this.infoState = state;
  };
  onResetInfoState = () => {
    this.infoState = initialInfoState;
  };

  downloadState: IDownloadState = initialDownloadState;
  onDownloadStart = () => {
    const downloadState = this.downloadState;
    if (!downloadState.isDownload) {
      downloadState.isDownload = true;
    }
  };
  onDownloadCancel = () => {
    const downloadState = this.downloadState;
    setWebBridge({
      name: "onDownloadCancel",
      args: downloadState.id,
    });
    this.downloadState = initialDownloadState;
    this.installState = initialInstallState;
  };
  onDownloadProgress = (args: string) => {
    const downloadState = this.downloadState;

    try {
      const data = convertStringToObject(args);
      if (downloadState.isDownload) {
        downloadState.id = data.id;
        downloadState.percentage = data.percentage;

        this.onErrorState({
          status: data.status,
          errorCode: data.error_code,
        });
      }
    } catch {
      this.onUpdateDetailError(GlobalErrorType.UNKNOWN);
    }
  };

  installState: IInstallState = initialInstallState;
  onInstallProgress = (args: string) => {
    try {
      const data = convertStringToObject(args);
      //Firebase Event
      const { percentage } = data;

      const installState = this.installState;
      const downloadState = this.downloadState;

      installState.id = data.id;

      if (percentage === 100) {
        downloadState.isDownload = false;
        installState.isInstall = true;
      }

      this.onErrorState({
        errorCode: data.error_code,
      });
    } catch {
      this.onUpdateDetailError(GlobalErrorType.UNKNOWN);
    }
  };
  onInstallComplete = (id: string) => {
    this.completedProjects = this.completedProjects.concat(id);
    this.downloadState = initialDownloadState;
    this.installState = initialInstallState;
  };
  completedProjects: string[] = [];

  updateState: IUpdateState = {
    isUpdate: false,
  };
  onUpdateStart = (type: string) => {
    this.updateState = {
      type,
      isUpdate: true,
    };
  };
  onUpdateCancel = () => {
    this.updateState = {
      type: "",
      isUpdate: false,
    };
  };

  // detail local error
  errorState: IErrorState = {};
  onErrorState = (state: IErrorInfo) => {
    // error 아닌 경우 return
    if (!state.status && !state.errorCode) return;

    // error 인 경우 error 정보 입력
    this.errorState = state;
    // download, install 초기화
    this.downloadState = initialDownloadState;
    this.installState = initialInstallState;
  };
  onResetErrorState = () => {
    this.errorState = {};
  };

  detailErrorState = GlobalErrorType.NONE;
  onUpdateDetailError = (state: GlobalErrorType) => {
    this.detailErrorState = state;
    this.detailState.isLoadingList = false;
    this.downloadState = initialDownloadState;
    this.installState = initialInstallState;
    this.onResetErrorState();
  };
}
